import { IPositionsState } from '../../project-opportunities/create-project-opportunity/CreateProjectOpportunity';
import { ChangeEvent, Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { ICreateProjectPosition, IProjectPosition } from '../../../interfaces/projectPosition';
import { ILanguageCode } from '../../../interfaces/languageCode';
import { IPositionWithSkills } from '../../../interfaces/position';
import { ColumnBoxFlexFullWidth, FlexBox, FlexBoxFullWidth } from '../boxes';
import { Box, IconButton, MenuItem, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import ExpandIcon from '../ExpandIcon';
import { Delete } from '@mui/icons-material';
import { TEXTS } from '../../../core/texts';
import { ValidationErrorState } from '../../../types/validationErrorState';
import { BorderBoxWithTitle } from '../BorderBoxWithTitle';
import { AVAILABILITY_TYPE_MAP, AvailabilityType } from '../../../enums/AvailabilityType';
import TimeSelectAndTimezone from '../TimeSelectAndTimezone';
import { SelectLanguageAndProficiency } from '../SelectLanguageAndProficiency';
import { PROFICIENCY_DESCRIPTIONS } from '../../../interfaces/language';
import { BaseObj } from '../../../types/baseObj';
import RateAvailability from '../RateAvailability';
import { TagsAutocomplete } from '../TagsAutocomplete';
import { getTruncatedName } from '../../../utils/getTruncatedName';
import { ControlledInputOnBlur } from '../ControlledInputOnBlur';
import { useNotification } from '../../../+state/useNotification';

interface IProps {
  position: ICreateProjectPosition | IProjectPosition;
  positionKey: string;
  setPositions: Dispatch<SetStateAction<IPositionsState<any>>>;
  languageCodes: ILanguageCode[],
  positionsWithSkills: IPositionWithSkills[],
  validationErrors?: ValidationErrorState,
  withHeader?: boolean,
  withSkills?: boolean
};

const ProjectPositionFormItem = ({
                                   positionKey,
                                   setPositions,
                                   position,
                                   positionsWithSkills,
                                   languageCodes,
                                   validationErrors = {},
                                   withHeader = true,
                                   withSkills = true,
                                 }: IProps) => {
  const theme = useTheme();
  const { openErrorToast } = useNotification();
  const [expanded, setExpanded] = useState(true);
  const toggleExpand = useCallback(() => setExpanded(prev => !prev), []);
  const isWithError = useMemo(() => !!Object.values(validationErrors).filter(v => !!v).length, [validationErrors]);
  const selectedPosition = useMemo(() => positionsWithSkills.find(pos => pos.id === position.positionId)?.name || '', [positionsWithSkills, position]);
  const selectedSkillsTitle = useMemo(() => (position as ICreateProjectPosition).skills ? (position as ICreateProjectPosition).skills.join(', ') : '', [position]);

  const removePosition = useCallback(() => setPositions(prev => {
    const { [positionKey]: toRemove, ...rest } = prev;
    return rest;
  }), [setPositions, positionKey]);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPositions(prev => ({ ...prev, [positionKey]: { ...prev[positionKey], [name]: value } }));
  }, [setPositions, positionKey]);

  const handleNestedInputChange = useCallback((nestedKey: keyof Omit<ICreateProjectPosition, 'skills'>, e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setPositions(prev => ({
      ...prev,
      [positionKey]: {
        ...prev[positionKey],
        [nestedKey]: {
          ...prev[positionKey][nestedKey] as unknown as any,
          [name]: type === 'number' ? +value : value
        }
      }
    }));
  }, [setPositions, positionKey]);

  const handleAvailabilityInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) =>
    handleNestedInputChange('availability', e), [handleNestedInputChange]);

  const handlePreferredRateInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) =>
    handleNestedInputChange('preferredRate', e), [handleNestedInputChange]);

  const handlePositionAndRoleIdChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const positionId = +e.target.value;
    const roleId = positionsWithSkills.find(p => p.id === positionId)?.roleId;
    if (typeof roleId !== 'number' || typeof positionId !== 'number') {
      return openErrorToast(TEXTS.SOMETHING_WENT_WRONG);
    }
    setPositions(prev => ({ ...prev, [positionKey]: { ...prev[positionKey], roleId, positionId } }));
  }, [openErrorToast, positionsWithSkills, setPositions, positionKey]);

  const handleLanguageChange = useCallback((val: BaseObj) => {
    let [key, value] = Object.entries(val)[0];
    if (key === 'minimumProficiencyLevel' && typeof value === 'number') {
      value = Object.values(PROFICIENCY_DESCRIPTIONS)[value - 1];
    }
    handleNestedInputChange('language', { target: { name: key, value } } as ChangeEvent<HTMLInputElement>);
  }, [handleNestedInputChange]);

  const skillsList = useMemo(() =>
    new Set(positionsWithSkills.map(p => p.skills.map(s => s.name)).flat() || []), [positionsWithSkills]);

  const handleSkillsUpdate = useCallback((skills: string[]) => setPositions(prev => ({
    ...prev,
    [positionKey]: { ...prev[positionKey], skills }
  })), [setPositions, positionKey]);

  const languageProficiencyLevel = useMemo(() => {
    const level = Object.values(PROFICIENCY_DESCRIPTIONS).indexOf(position.language.minimumProficiencyLevel) + 1;
    return level > 0 ? level : '' as any;
  }, [position.language.minimumProficiencyLevel]);

  return (
    <ColumnBoxFlexFullWidth border={isWithError ? `1px solid ${theme.palette.error.main}` : 'unset'}
                            boxShadow={theme.shadows[2]} borderRadius={theme.shape.borderRadius} p={1} my={1}>
      {
        withHeader &&
        <FlexBoxFullWidth alignItems={{ mobile: 'flex-start', tablet: 'center' }} justifyContent={'space-between'}
                          flexDirection={{ mobile: 'column', tablet: 'row' }}>
          <FlexBox alignItems={'center'}>

            <Tooltip title={'Remove position'} sx={{ mr: 1 }}>
              <IconButton onClick={removePosition} color={'error'}>
                <Delete/>
              </IconButton>
            </Tooltip>
            <Typography>
              {`${selectedPosition} ${selectedSkillsTitle ? ('- ' + getTruncatedName(selectedSkillsTitle, 30)) : ''}`}
            </Typography>
          </FlexBox>

          <Box alignSelf={'center'}>
            <ExpandIcon iconColor={theme.palette.primary.main} downDirection={true} expanded={expanded}
                        onClick={toggleExpand} tooltip={expanded ? 'collapse' : 'expand'}
            />
          </Box>

        </FlexBoxFullWidth>
      }
      {
        expanded &&
        <ColumnBoxFlexFullWidth mt={2} p={1}>
          <TextField name={'positionId'} label={TEXTS.POSITION} required
                     value={positionsWithSkills.length ? position.positionId : ''} select
                     onChange={handlePositionAndRoleIdChange} sx={{ my: 1 }} variant={'standard'}
          >
            {
              positionsWithSkills.map(position => (
                <MenuItem key={position.name + position.id} value={position.id}>{position.name}</MenuItem>
              ))
            }
          </TextField>
          <ControlledInputOnBlur name={'title'} label={TEXTS.TITLE} onChange={handleInputChange} value={position.title}
                                 error={!!validationErrors?.title} helperText={validationErrors?.title} sx={{ my: 1 }}
                                 variant={'standard'}
          />
          <ControlledInputOnBlur name={'link'} value={position.link} label={TEXTS.LINK} onChange={handleInputChange}
                                 error={!!validationErrors?.link} helperText={validationErrors?.link} sx={{ my: 1 }}
                                 variant={'standard'}
          />
          <ControlledInputOnBlur name={'description'} value={position.description} label={TEXTS.DESCRIPTION}
                                 onChange={handleInputChange}
                                 error={!!validationErrors?.description} helperText={validationErrors?.description}
                                 sx={{ my: 1 }}
                                 variant={'standard'} multiline rows={3}
          />

          {
            withSkills && typeof position.skills[0] !== 'object' &&
            <Box my={2}>
              <TagsAutocomplete
                list={[...skillsList]}
                onChange={handleSkillsUpdate}
                value={(position as ICreateProjectPosition).skills}
                label={'Skills'}
                error={validationErrors?.skills || undefined}
              />
            </Box>
          }

          <BorderBoxWithTitle title={'Preferred Rate'} borderStyle={'dotted'} py={3} pt={4}>
            <RateAvailability
              rateAmount={position.preferredRate.amount}
              rateCurrency={position.preferredRate.currency}
              rateType={position.preferredRate.type}
              handleInputChange={handlePreferredRateInputChange}
              validationErrors={validationErrors}
              disabled={false}
              amountNamespace={'amount'}
              currencyNamespace={'currency'}
            />
          </BorderBoxWithTitle>

          <BorderBoxWithTitle title={'Availability'} borderStyle={'dotted'}>
            <TextField name={'availabilityType'} value={position.availability.availabilityType} label={TEXTS.TYPE}
                       onChange={handleAvailabilityInputChange}
                       error={!!validationErrors?.availabilityType} helperText={validationErrors?.availabilityType}
                       sx={{ my: 1 }}
                       variant={'standard'} select
            >
              {
                Object.values(AvailabilityType).map(type => (
                  <MenuItem key={type} value={type}>{AVAILABILITY_TYPE_MAP[type]}</MenuItem>
                ))
              }
            </TextField>

            <TimeSelectAndTimezone
              timezone={position.availability.timezone}
              validationErrors={validationErrors}
              disabled={false}
              fromHour={position.availability.fromHour}
              toHour={position.availability.toHour}
              handleInputChange={handleAvailabilityInputChange}
            />
          </BorderBoxWithTitle>

          <BorderBoxWithTitle title={'Language'} borderStyle={'dotted'}>
            <SelectLanguageAndProficiency
              list={languageCodes}
              languageCode={position.language.languageCode}
              proficiencyLevel={languageProficiencyLevel}
              onChange={handleLanguageChange}
              validationErrors={validationErrors}
              proficiencyNamespace={'minimumProficiencyLevel'}
            />
          </BorderBoxWithTitle>

        </ColumnBoxFlexFullWidth>
      }
    </ColumnBoxFlexFullWidth>
  );
};

export default ProjectPositionFormItem;